import { useEffect, useState } from "react"

import { cn } from "@/lib/utils"
import { CountdownText, useCountdown } from "./Countdown"
import { SubscribeButton } from "./PriceCard"
import { formatDollars } from "./format"

// The offer page's floating Subscribe bar. It shows once the hero has
// scrolled away and hides again while the closing price card is on screen, so
// there is never a second Subscribe beside a visible one. It spans the page
// column (measured from `wrapRef`), not the viewport, so it clears the rail.
// While hidden it is out of the accessibility tree and the tab order.
export default function StickyBuyBar({ plan, endpoints, closesAt, heroRef, closeRef, wrapRef }) {
  const left = useCountdown(closesAt)
  const [heroOn, setHeroOn] = useState(true)
  const [closeOn, setCloseOn] = useState(false)
  const [box, setBox] = useState(null)

  useEffect(() => {
    const hero = heroRef.current
    const close = closeRef.current
    if (!hero || typeof IntersectionObserver === "undefined") return undefined
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target === hero) setHeroOn(e.isIntersecting)
        if (e.target === close) setCloseOn(e.isIntersecting)
      })
    })
    io.observe(hero)
    if (close) io.observe(close)
    return () => io.disconnect()
  }, [heroRef, closeRef])

  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return undefined
    const place = () => {
      const r = wrap.getBoundingClientRect()
      const pad = parseFloat(getComputedStyle(wrap).paddingLeft) || 0
      setBox({ left: r.left + pad, width: Math.min(r.width - pad * 2, 1060) })
    }
    place()
    window.addEventListener("resize", place)
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place)
    ro?.observe(wrap)
    return () => {
      window.removeEventListener("resize", place)
      ro?.disconnect()
    }
  }, [wrapRef])

  const on = !heroOn && !closeOn

  return (
    <div
      data-testid="insider-buy-bar"
      aria-hidden={!on}
      inert={!on}
      style={box ?? undefined}
      className={cn(
        "fixed bottom-4 z-[80] flex items-center gap-[14px] rounded-[14px] border border-line-2 bg-card py-[9px] pr-[9px] pl-[18px] shadow-[0_18px_40px_-18px_rgba(0,0,0,0.35)] transition-[opacity,transform] duration-200 motion-reduce:transition-none",
        on ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0"
      )}
    >
      <div className="min-w-0 flex-1 text-[13px] font-semibold text-muted-foreground tabular-nums">
        <b className="text-foreground">Insider · {formatDollars(plan?.amountCents)}/month</b>
        {left && (
          <span className="max-[561px]:hidden">
            {" "}
            · closes in <CountdownText left={left} />
          </span>
        )}
      </div>
      <SubscribeButton endpoints={endpoints} tabIndex={on ? 0 : -1} className="px-[14px] py-[9px] text-[13px]" />
    </div>
  )
}
